"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";
import { Globe } from "@/components/magicui/globe";
import { variantBackgroundColors } from "@/lib/utils";

interface Feature24Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature24 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 24",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  attributes,
}: Feature24Props) => {

  const BlockBackground = variantBackgroundColors(darkMode, variant);   

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature24"
      attributes={attributes}
    >

      <div className="container">
        <div className={cn("relative flex w-full flex-col justify-between overflow-hidden rounded-2xl border px-6 py-10 pb-40 md:p-20 md:flex-row min-h-100", BlockBackground)} >
          <div className="flex h-full w-full md:max-w-[50%] flex-col justify-center gap-4">
            <div className="flex flex-col justify-between gap-6 items-center text-center md:text-left md:items-start">
              {title && (
                <> 
                  {badge && (
                    <Badge variant="outline" bold>
                      {badge}
                    </Badge>
                  )}
                  <h2>
                    {parse(title)}
                  </h2>
                </>
              )}
              {(content || hasButton) && (
                <>  
                  {content && (
                    <div className="text-muted-foreground">
                      {parse(content)}
                    </div>
                  )}
                  {hasButton && (
                    <div className="flex flex-col md:flex-row gap-4 w-full justify-center md:justify-start">
                      <div className="flex flex-col">
                        <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                          <Link href={buttonLink ?? ""}>
                            {buttonText}
                          </Link>
                        </Button>
                        {microCopy && (
                          <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                            {microCopy}
                          </p>
                        )}
                      </div>
                      {secondaryButton && (
                        <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                          <Link href={secondaryButtonLink ?? ""}>
                            {secondaryButtonText}
                          </Link>
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="relative size-full">
            <Globe className="absolute top-15 md:top-10 md:-right-80 lg:-right-150 scale-150 md:scale-200 xl:scale-160" darkMode={darkMode} variant={variant} revert/>
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
};

export { Feature24 };